import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Box,
    Typography,
    Alert,
    Divider,
    Grid,
    Tooltip,
    CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../utils/config';

const baseRow = () => ({
    responsable: '',
    nombreOperador: '',
    fechaInicio: '',
    fechaFinal: '',
    horaInicio: '',
    horaFin: '',
    codigoMaquina: '',
    eficiencia: '',
    dosisTeorica: '',
    volumenAguaL: '',
    dosisProductoLHa: '',
    producto: '',
    humedadCultivo: '',
    tchEstimado: ''
});

function toCleanStr(v) {
    if (v === null || v === undefined) return '';
    return String(v).trim();
}

function toUniqueList(arr) {
    const a = Array.isArray(arr) ? arr : [];
    const cleaned = a.map(toCleanStr).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const x of cleaned) {
        const key = x.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            out.push(x);
        }
    }
    return out;
}

function getAnyList(obj, keys = []) {
    if (!obj) return [];
    for (const k of keys) {
        const v = obj[k];
        if (Array.isArray(v)) return v;
        if (typeof v === 'string' || typeof v === 'number') return [String(v)];
    }
    return [];
}

function applyDefaultsToRows(rows, defaults) {
    const d = defaults || {};
    return (Array.isArray(rows) ? rows : []).map((r) => {
        const rr = { ...r };
        if (!toCleanStr(rr.responsable) && toCleanStr(d.responsable)) rr.responsable = d.responsable;
        if (!toCleanStr(rr.nombreOperador) && toCleanStr(d.nombreOperador)) rr.nombreOperador = d.nombreOperador;
        if (!toCleanStr(rr.codigoMaquina) && toCleanStr(d.codigoMaquina)) rr.codigoMaquina = d.codigoMaquina;
        if (!toCleanStr(rr.producto) && toCleanStr(d.producto)) rr.producto = d.producto;
        return rr;
    });
}

function normalizePreloaded(preloaded) {
    const src = preloaded || {};
    return {
        responsableOptions: toUniqueList(src.responsableOptions),
        operadorOptions: toUniqueList(src.operadorOptions),
        codigoMaquinaOptions: toUniqueList(src.codigoMaquinaOptions),
        productoOptions: toUniqueList(src.productoOptions)
    };
}

function mergePreloaded(base, incoming) {
    const a = normalizePreloaded(base);
    const b = normalizePreloaded(incoming);
    return {
        responsableOptions: toUniqueList([...a.responsableOptions, ...b.responsableOptions]),
        operadorOptions: toUniqueList([...a.operadorOptions, ...b.operadorOptions]),
        codigoMaquinaOptions: toUniqueList([...a.codigoMaquinaOptions, ...b.codigoMaquinaOptions]),
        productoOptions: toUniqueList([...a.productoOptions, ...b.productoOptions])
    };
}

function hasAnyPreloadedData(preloaded) {
    const p = preloaded || {};
    return Boolean(
        p.responsableOptions?.length ||
        p.operadorOptions?.length ||
        p.codigoMaquinaOptions?.length ||
        p.productoOptions?.length
    );
}

function buildDefaults(preloaded) {
    const p = normalizePreloaded(preloaded);

    const lastMachine = toCleanStr(localStorage.getItem(LS_LAST_MACHINE));
    const lastProd = toCleanStr(localStorage.getItem(LS_LAST_PRODUCT));
    const lastOper = toCleanStr(localStorage.getItem(LS_LAST_OPERADOR));
    const lastResp = toCleanStr(localStorage.getItem(LS_LAST_RESPONSABLE));

    const pick = (lastVal, options) => {
        if (lastVal) return lastVal;
        const arr = Array.isArray(options) ? options : [];
        return arr.length > 0 ? arr[0] : '';
    };

    return {
        responsable: pick(lastResp, p.responsableOptions),
        nombreOperador: pick(lastOper, p.operadorOptions),
        codigoMaquina: pick(lastMachine, p.codigoMaquinaOptions),
        producto: pick(lastProd, p.productoOptions)
    };
}

function getStoredUserData() {
    try {
        return JSON.parse(localStorage.getItem('userData')) || {};
    } catch {
        return {};
    }
}

function getFallbackResponsable(userData) {
    return (
        toCleanStr(userData?.nombre) ||
        toCleanStr(userData?.name) ||
        toCleanStr(userData?.username) ||
        toCleanStr(userData?.email) ||
        ''
    );
}

const LS_LAST_MACHINE = 'aps_last_codigoMaquina';
const LS_LAST_PRODUCT = 'aps_last_producto';
const LS_LAST_OPERADOR = 'aps_last_nombreOperador';
const LS_LAST_RESPONSABLE = 'aps_last_responsable';

const LS_FILTERS_CACHE_KEY = (userId) => `aps_filters_cache_v2_${userId}`;
const LS_PRELOADED_CACHE_KEY = (userId) => `aps_preloaded_cache_v2_${userId}`;
const FILTERS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const PRELOADED_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function readTimedCache(cacheKey, ttlMs) {
    try {
        const raw = localStorage.getItem(cacheKey);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || !obj.ts) return null;
        if (Date.now() - obj.ts > ttlMs) return null;
        return obj;
    } catch {
        return null;
    }
}

function writeTimedCache(cacheKey, data) {
    try {
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), ...data }));
    } catch {
        // ignore
    }
}

function buildSeedPreloaded(userData) {
    const userId = userData?.userId;
    const cachedFilters = userId ? readTimedCache(LS_FILTERS_CACHE_KEY(userId), FILTERS_CACHE_TTL_MS) : null;
    const cachedPreloaded = userId ? readTimedCache(LS_PRELOADED_CACHE_KEY(userId), PRELOADED_CACHE_TTL_MS) : null;
    const fallbackResp = getFallbackResponsable(userData);

    return normalizePreloaded({
        responsableOptions: [
            localStorage.getItem(LS_LAST_RESPONSABLE),
            ...(cachedPreloaded?.responsableOptions || []),
            fallbackResp
        ],
        operadorOptions: [
            localStorage.getItem(LS_LAST_OPERADOR),
            ...(cachedPreloaded?.operadorOptions || []),
            ...(cachedFilters?.operadorOptions || [])
        ],
        codigoMaquinaOptions: [
            localStorage.getItem(LS_LAST_MACHINE),
            ...(cachedPreloaded?.codigoMaquinaOptions || [])
        ],
        productoOptions: [
            localStorage.getItem(LS_LAST_PRODUCT),
            ...(cachedPreloaded?.productoOptions || []),
            ...(cachedFilters?.productoOptions || [])
        ]
    });
}

const AXIOS_TIMEOUT_MS = 60000;
const SPINNER_MAX_VISIBLE_MS = 1800;

const AplicacionesAereasModal = ({ open, onClose, onConfirm, initialRows = [], title = 'Datos de Aplicaciones Aéreas', description = null }) => {
    const [rows, setRows] = useState([baseRow()]);
    const [error, setError] = useState(null);
    const [loadingPreloaded, setLoadingPreloaded] = useState(false);
    const [preloaded, setPreloaded] = useState(
        normalizePreloaded({
            responsableOptions: [],
            operadorOptions: [],
            codigoMaquinaOptions: [],
            productoOptions: []
        })
    );

    const loadSeqRef = useRef(0);
    const abortRef = useRef(null);

    useEffect(() => {
        if (!open) return;

        loadSeqRef.current += 1;
        const seq = loadSeqRef.current;

        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch {
                // ignore
            }
        }

        const controller = new AbortController();
        abortRef.current = controller;
        let spinnerTimer = null;

        const userData = getStoredUserData();
        const userId = userData?.userId;

        const seedPreloaded = buildSeedPreloaded(userData);
        const normalizedRows = Array.isArray(initialRows) && initialRows.length > 0
            ? initialRows.map((r) => ({ ...baseRow(), ...r }))
            : [baseRow()];

        setPreloaded(seedPreloaded);
        setRows(applyDefaultsToRows(normalizedRows, buildDefaults(seedPreloaded)));
        setError(null);

        const showVisualLoader = Boolean(userId) && !hasAnyPreloadedData(seedPreloaded);
        setLoadingPreloaded(showVisualLoader);

        if (showVisualLoader) {
            spinnerTimer = setTimeout(() => {
                if (loadSeqRef.current === seq) setLoadingPreloaded(false);
            }, SPINNER_MAX_VISIBLE_MS);
        }

        if (!userId) {
            return () => {
                if (spinnerTimer) clearTimeout(spinnerTimer);
                try {
                    controller.abort();
                } catch {
                    // ignore
                }
                if (abortRef.current === controller) abortRef.current = null;
            };
        }

        const axiosCfg = { timeout: AXIOS_TIMEOUT_MS, signal: controller.signal };

        const run = async () => {
            let nextPreloaded = seedPreloaded;

            try {
                const [preloadedResult, filtrosResult] = await Promise.allSettled([
                    axios.get(`${API_BASE_URL}configuration/preloaded-properties/${userId}`, axiosCfg),
                    axios.get(`${API_BASE_URL}dashboard/filtros/${userId}`, axiosCfg)
                ]);

                if (preloadedResult.status === 'fulfilled') {
                    const resp = preloadedResult.value;
                    const props = resp?.data?.properties || resp?.data?.data?.properties || {};
                    const parsedPreloaded = normalizePreloaded({
                        responsableOptions: [
                            ...getAnyList(props, ['responsible', 'responsable', 'RESPONSABLE', 'owner', 'encargado']),
                            getFallbackResponsable(userData)
                        ],
                        operadorOptions: getAnyList(props, ['operator', 'operador', 'operadorCM', 'nombreOperador', 'NOMBRE_OPERADOR']),
                        codigoMaquinaOptions: getAnyList(props, ['machineCode', 'codigoMaquina', 'CODIGO_MAQUINA', 'machine']),
                        productoOptions: getAnyList(props, ['product', 'producto', 'PRODUCTO'])
                    });

                    if (parsedPreloaded.responsableOptions.length || parsedPreloaded.operadorOptions.length || parsedPreloaded.codigoMaquinaOptions.length || parsedPreloaded.productoOptions.length) {
                        writeTimedCache(LS_PRELOADED_CACHE_KEY(userId), parsedPreloaded);
                    }

                    nextPreloaded = mergePreloaded(nextPreloaded, parsedPreloaded);
                } else {
                    const e = preloadedResult.reason;
                    if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
                        console.error('Error cargando preloaded-properties APS:', e);
                    }
                }

                if (filtrosResult.status === 'fulfilled') {
                    const data = filtrosResult.value?.data || {};
                    const parsedFilters = normalizePreloaded({
                        operadorOptions: getAnyList(data, ['operador', 'operadores', 'operator', 'operators']),
                        productoOptions: getAnyList(data, ['producto', 'productos', 'product', 'products'])
                    });

                    if (parsedFilters.operadorOptions.length || parsedFilters.productoOptions.length) {
                        writeTimedCache(LS_FILTERS_CACHE_KEY(userId), {
                            operadorOptions: parsedFilters.operadorOptions,
                            productoOptions: parsedFilters.productoOptions
                        });
                    }

                    nextPreloaded = mergePreloaded(nextPreloaded, parsedFilters);
                } else {
                    const e = filtrosResult.reason;
                    if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
                        console.error('Error cargando dashboard/filtros:', e);
                    }
                }

                if (loadSeqRef.current === seq && !controller.signal.aborted) {
                    setPreloaded(nextPreloaded);
                    setRows((prev) => applyDefaultsToRows(prev, buildDefaults(nextPreloaded)));
                }
            } finally {
                if (spinnerTimer) clearTimeout(spinnerTimer);
                if (loadSeqRef.current === seq) setLoadingPreloaded(false);
            }
        };

        run();

        return () => {
            if (spinnerTimer) clearTimeout(spinnerTimer);
            try {
                controller.abort();
            } catch {
                // ignore
            }
            if (abortRef.current === controller) abortRef.current = null;
        };
    }, [open, initialRows]);

    const persistLastUsed = (key, value) => {
        const v = toCleanStr(value);
        if (key === 'codigoMaquina') localStorage.setItem(LS_LAST_MACHINE, v);
        if (key === 'producto') localStorage.setItem(LS_LAST_PRODUCT, v);
        if (key === 'nombreOperador') localStorage.setItem(LS_LAST_OPERADOR, v);
        if (key === 'responsable') localStorage.setItem(LS_LAST_RESPONSABLE, v);
    };

    const addRow = () => {
        const newRow = applyDefaultsToRows([baseRow()], buildDefaults(preloaded))[0];
        setRows((prev) => [...prev, newRow]);
    };

    const removeRow = (idx) => {
        setRows((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateField = (idx, key, value) => {
        const v = value ?? '';
        if (key === 'codigoMaquina' || key === 'producto' || key === 'nombreOperador' || key === 'responsable') {
            persistLastUsed(key, v);
        }
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: v } : r)));
    };

    const validate = () => {
        if (!rows.length) {
            setError('Debes ingresar al menos una fila.');
            return false;
        }
        setError(null);
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;
        onConfirm(rows);
    };

    const handleClose = () => {
        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch {
                // ignore
            }
            abortRef.current = null;
        }
        setLoadingPreloaded(false);
        setError(null);
        if (typeof onClose === 'function') onClose();
    };

    const isSameOption = (option, value) =>
        toCleanStr(option).toLowerCase() === toCleanStr(value).toLowerCase();

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{title}</Typography>
                    {loadingPreloaded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                                Cargando valores guardados…
                            </Typography>
                        </Box>
                    )}
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Los campos <b>Responsable</b>, <b>Nombre de operador</b>, <b>Producto</b> y <b>Codigo de maquina</b> se autocompletan si hay
                    valores guardados. Puedes editarlos libremente.
                </Alert>

                {rows.map((row, idx) => (
                    <Box key={idx} sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1">Fila {idx + 1}</Typography>
                            <Tooltip title="Eliminar fila">
                                <IconButton color="error" onClick={() => removeRow(idx)} disabled={rows.length === 1}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={preloaded.responsableOptions}
                                    value={toCleanStr(row.responsable)}
                                    isOptionEqualToValue={isSameOption}
                                    onChange={(e, newVal) => updateField(idx, 'responsable', newVal ?? '')}
                                    onInputChange={(e, newInput) => updateField(idx, 'responsable', newInput ?? '')}
                                    renderInput={(params) => <TextField {...params} label="Responsable" fullWidth />}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={preloaded.operadorOptions}
                                    value={toCleanStr(row.nombreOperador)}
                                    isOptionEqualToValue={isSameOption}
                                    onChange={(e, newVal) => updateField(idx, 'nombreOperador', newVal ?? '')}
                                    onInputChange={(e, newInput) => updateField(idx, 'nombreOperador', newInput ?? '')}
                                    renderInput={(params) => <TextField {...params} label="Nombre de operador" fullWidth />}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Fecha inicio"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={row.fechaInicio}
                                    onChange={(e) => updateField(idx, 'fechaInicio', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Fecha final"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={row.fechaFinal}
                                    onChange={(e) => updateField(idx, 'fechaFinal', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Hora inicio"
                                    type="time"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={row.horaInicio}
                                    onChange={(e) => updateField(idx, 'horaInicio', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Hora final"
                                    type="time"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={row.horaFin}
                                    onChange={(e) => updateField(idx, 'horaFin', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={preloaded.codigoMaquinaOptions}
                                    value={toCleanStr(row.codigoMaquina)}
                                    isOptionEqualToValue={isSameOption}
                                    onChange={(e, newVal) => updateField(idx, 'codigoMaquina', newVal ?? '')}
                                    onInputChange={(e, newInput) => updateField(idx, 'codigoMaquina', newInput ?? '')}
                                    renderInput={(params) => <TextField {...params} label="Codigo de maquina" fullWidth />}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Eficiencia"
                                    fullWidth
                                    value={row.eficiencia}
                                    onChange={(e) => updateField(idx, 'eficiencia', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Volumen teórico"
                                    fullWidth
                                    value={row.dosisTeorica}
                                    onChange={(e) => updateField(idx, 'dosisTeorica', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Volumen de agua"
                                    fullWidth
                                    value={row.volumenAguaL}
                                    onChange={(e) => updateField(idx, 'volumenAguaL', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Dosis de producto"
                                    fullWidth
                                    value={row.dosisProductoLHa}
                                    onChange={(e) => updateField(idx, 'dosisProductoLHa', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={preloaded.productoOptions}
                                    value={toCleanStr(row.producto)}
                                    isOptionEqualToValue={isSameOption}
                                    onChange={(e, newVal) => updateField(idx, 'producto', newVal ?? '')}
                                    onInputChange={(e, newInput) => updateField(idx, 'producto', newInput ?? '')}
                                    renderInput={(params) => <TextField {...params} label="Producto" fullWidth />}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Humedad del cultivo"
                                    fullWidth
                                    value={row.humedadCultivo}
                                    onChange={(e) => updateField(idx, 'humedadCultivo', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="TCH estimado"
                                    fullWidth
                                    value={row.tchEstimado}
                                    onChange={(e) => updateField(idx, 'tchEstimado', e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined">
                    Añadir fila
                </Button>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    {description || 'La plantilla CSV/Excel ya no se utiliza para Aplicaciones Aéreas. Guarda aquí los datos manuales antes de ejecutar el análisis.'}
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant="contained">
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AplicacionesAereasModal;
